import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import saApi from "../lib/saApi";
import {
  Loader2, DollarSign, ShieldAlert, Eye, Flame, Trophy, TrendingUp,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => (n ?? 0).toLocaleString();
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

const RANK_BADGE = ["#F87171", "#FBBF24", "#22D3EE"]; // 1st gold-ish, 2nd amber, 3rd cyan

function Leaderboard({ title, icon: Icon, tint, rows, valueKey, valueRender, empty, testid, rowIdPrefix }) {
  return (
    <div data-testid={testid}
         className="rounded-2xl p-5 border border-white/10 backdrop-blur-md flex flex-col"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tint}22` }}>
          <Icon className="w-4 h-4" style={{ color: tint }} />
        </div>
        <h3 className="font-display text-white">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-white/40 text-sm py-6">{empty}</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.tenant_id} data-testid={`${rowIdPrefix}-row-${i}`}
                className="flex items-center gap-3 rounded-xl px-3 py-2 bg-white/5 border border-white/5 hover:border-white/15 transition">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white"
                   style={{ background: i < 3 ? RANK_BADGE[i] : "rgba(255,255,255,0.10)",
                            color: i < 3 ? "#0B0F14" : "rgba(255,255,255,0.70)" }}>
                {i + 1}
              </div>
              <span className="flex-1 min-w-0 truncate text-sm text-white">{r.tenant_name}</span>
              <span className="text-sm font-mono tabular-nums" style={{ color: tint }}>
                {valueRender ? valueRender(r) : fmt(r[valueKey])}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function SAInsights() {
  const [days, setDays] = useState("30");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    saApi.get("/insights", { params: { days } })
      .then(({ data }) => setD(data))
      .catch(() => setD({ ai_cost: [], failed_logins: [], dashboard_views: [], risk: [] }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <SuperAdminShell
      title="Cross-Tenant Insights"
      actions={
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="sa-insights-window" className="w-[140px] bg-white/5 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <div className="px-5 md:px-8 py-6 space-y-6">
        {loading && !d ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" />Crunching…</div>
        ) : (
          <>
            <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
                 style={{ background: "linear-gradient(90deg, rgba(248,113,113,0.10), rgba(167,139,250,0.06))" }}>
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-5 h-5 text-red-300" />
                <h2 className="font-display text-lg text-white">Composite Risk Ranking</h2>
                <span className="text-[10px] text-white/40 uppercase tracking-widest ml-2">
                  weighted: failed logins ×1.5, AI cost ×1.0, views ×0.25
                </span>
              </div>
              {(d?.risk || []).length === 0 ? (
                <div className="text-white/50 text-sm py-4">No signal in the last {d?.days || days} days.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {d.risk.map((r, i) => (
                    <div key={r.tenant_id} data-testid={`sa-risk-card-${i}`}
                         className="rounded-xl p-3 bg-white/5 border border-white/10 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                           style={{ background: i < 3 ? RANK_BADGE[i] : "rgba(255,255,255,0.08)",
                                    color: i < 3 ? "#0B0F14" : "rgba(255,255,255,0.7)" }}>
                        {i + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-white text-sm truncate">{r.tenant_name}</div>
                        <div className="text-[10px] text-white/50 uppercase tracking-widest">
                          {[
                            r.ai_rank ? `ai #${r.ai_rank}` : null,
                            r.failed_login_rank ? `fails #${r.failed_login_rank}` : null,
                            r.views_rank ? `views #${r.views_rank}` : null,
                          ].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-display text-white">{r.score}</div>
                        <div className="text-[10px] text-white/40 uppercase tracking-widest">risk</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Leaderboard
                testid="sa-lb-ai-cost"
                rowIdPrefix="sa-lb-ai-cost"
                title="AI Spend (Top 10)"
                icon={DollarSign}
                tint="#34D399"
                rows={d?.ai_cost || []}
                valueKey="cost_usd"
                valueRender={(r) => `${usd(r.cost_usd)} · ${fmt(r.calls)}c`}
                empty="No AI activity in this window."
              />
              <Leaderboard
                testid="sa-lb-fails"
                rowIdPrefix="sa-lb-fails"
                title="Failed Logins (24h)"
                icon={ShieldAlert}
                tint="#F87171"
                rows={d?.failed_logins || []}
                valueKey="failures"
                empty="No failed sign-ins in the last day."
              />
              <Leaderboard
                testid="sa-lb-views"
                rowIdPrefix="sa-lb-views"
                title="Dashboard Activity"
                icon={Eye}
                tint="#22D3EE"
                rows={d?.dashboard_views || []}
                valueKey="views"
                empty="No dashboard activity in this window."
              />
            </div>

            <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md flex items-start gap-3"
                 style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))" }}>
              <TrendingUp className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
              <div className="text-sm text-white/70 leading-relaxed">
                <b className="text-white">How to read this.</b> The composite score gives each tenant a
                weighted rank across the three dimensions — a tenant that dominates one dimension can still
                surface even if quiet elsewhere. Use the tenant filter on any mirrored page (Audit, Login
                Activity, Usage) to drill into a specific customer.
              </div>
            </div>
          </>
        )}
      </div>
    </SuperAdminShell>
  );
}
